/* Stairlift Q&A hub and question pages: sticky header offset, in-page search, active category tracking. */

// The theme's announcement bar and header can both be sticky. Expose their combined height as
// --qa-sticky-offset so anchors and the sticky sidebar clear them.
if (!window.qaStickyOffset) {
  window.qaStickyOffset = () => {
    let offset = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
      if (getComputedStyle(section).position === 'sticky') offset += section.offsetHeight;
    });
    document.documentElement.style.setProperty('--qa-sticky-offset', `${offset}px`);
    return offset;
  };

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(window.qaStickyOffset, 150);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.qaStickyOffset);
  } else {
    window.qaStickyOffset();
  }
}

if (!customElements.get('qa-hub')) {
  class QaHub extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-qa-search-form]');
      this.input = this.querySelector('[data-qa-search-input]');
      this.status = this.querySelector('[data-qa-search-status]');
      this.noResults = this.querySelector('[data-qa-no-results]');
      this.categories = Array.from(this.querySelectorAll('[data-qa-category]'));
      this.items = Array.from(this.querySelectorAll('[data-qa-item]'));
      this.ctas = Array.from(this.querySelectorAll('[data-qa-cta]'));
      this.navItems = Array.from(this.querySelectorAll('[data-qa-nav-item]'));
      this.jumpLinks = Array.from(this.querySelectorAll('[data-qa-jump-link]'));

      // Cache searchable text once; the content does not change after load.
      this.index = this.items.map((item) => ({ item, text: this.normalise(item.textContent) }));

      this.stickyOffset = window.qaStickyOffset();

      if (this.form && this.input) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.search(this.input.value);
          const first = this.items.find((item) => !item.hidden);
          if (first) first.scrollIntoView({ behavior: this.scrollBehaviour(), block: 'start' });
        });
        this.input.addEventListener('input', this.debounce(() => this.search(this.input.value), 150));
        this.input.addEventListener('search', () => this.search(this.input.value));
      }

      this.querySelectorAll('[data-qa-search-clear]').forEach((button) =>
        button.addEventListener('click', () => {
          if (!this.input) return;
          this.input.value = '';
          this.search('');
          this.input.focus();
        })
      );

      this.observeCategories();

      const query = new URLSearchParams(window.location.search).get('q');
      if (query && this.input) {
        this.input.value = query;
        this.search(query);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    normalise(value) {
      return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[’']/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    search(rawQuery) {
      const query = this.normalise(rawQuery || '');
      // Each term must match the start of a word, so "vat" finds "VAT" but not "private".
      const terms = query
        .split(' ')
        .filter(Boolean)
        .map((term) => new RegExp(`(^|[^a-z0-9£])${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));
      const searching = terms.length > 0;
      let total = 0;

      this.index.forEach(({ item, text }) => {
        const match = !searching || terms.every((term) => term.test(text));
        item.hidden = !match;
        if (match) total += 1;
      });

      this.categories.forEach((category) => {
        const visible = category.querySelectorAll('[data-qa-item]:not([hidden])').length;
        category.hidden = visible === 0;
        const navItems = this.navItems.filter((el) => el.dataset.qaNavItem === category.dataset.qaCategory);
        navItems.forEach((navItem) => {
          navItem.hidden = visible === 0;
          const count = navItem.querySelector('[data-qa-count]');
          if (count) count.textContent = visible;
        });
      });

      this.ctas.forEach((cta) => (cta.hidden = searching));
      if (this.noResults) this.noResults.hidden = !searching || total > 0;

      if (this.status) {
        if (!searching) {
          this.status.textContent = '';
        } else {
          const template = total === 1 ? this.dataset.resultsOne : this.dataset.resultsMany;
          this.status.textContent = (template || '[count] results')
            .replace('[count]', total)
            .replace('[query]', (rawQuery || '').trim());
        }
      }
    }

    observeCategories() {
      if (!this.jumpLinks.length || !('IntersectionObserver' in window)) return;

      const visible = new Map();
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => visible.set(entry.target, entry.isIntersecting));
          const current = this.categories.find((category) => !category.hidden && visible.get(category));
          if (current) this.setActive(current.id);
        },
        { rootMargin: `-${this.stickyOffset + 24}px 0px -55% 0px` }
      );
      this.categories.forEach((category) => this.observer.observe(category));
    }

    setActive(id) {
      this.jumpLinks.forEach((link) => {
        if (link.getAttribute('href') === `#${id}`) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    scrollBehaviour() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('qa-hub', QaHub);
}
